import React, { useRef, useState } from 'react';
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap';
import api from '../../lib/api';
import { useToast } from './ToastProvider';

export default function ImageUploadField({
  label,
  value,
  placeholder,
  onChange,
  kind = 'brand',
}) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);
  const { showToast } = useToast();

  const compressToTarget = (file, targetBytes = 50 * 1024) =>
    new Promise((resolve) => {
      if (!file.type?.startsWith('image/') || file.type.includes('svg')) {
        resolve(file);
        return;
      }
      const img = new Image();
      const reader = new FileReader();
      reader.onload = (e) => {
        img.onload = () => {
          let quality = 0.9;
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const maxSide = 1600;
          if (Math.max(width, height) > maxSide) {
            const ratio = maxSide / Math.max(width, height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          const step = () => {
            canvas.toBlob(
              (blob) => {
                if (!blob) {
                  resolve(file);
                  return;
                }
                if (blob.size <= targetBytes || quality <= 0.3) {
                  resolve(new File([blob], file.name.replace(/\.\w+$/, '.jpg'), { type: 'image/jpeg' }));
                  return;
                }
                quality -= 0.1;
                step();
              },
              'image/jpeg',
              quality
            );
          };
          step();
        };
        img.src = e.target?.result;
      };
      reader.readAsDataURL(file);
    });

  const uploadFile = async (file) => {
    if (!file) return;
    const compressedFile = await compressToTarget(file);
    const formData = new FormData();
    formData.append('file', compressedFile);
    formData.append('kind', kind);

    setUploading(true);
    try {
      const { data } = await api.post('/upload/image/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      onChange(data?.url || '');
      showToast('Image uploaded successfully.', 'success');
    } catch (error) {
      showToast(error?.response?.data?.error || 'Image upload failed.', 'danger');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <Form.Group>
      <Form.Label>{label}</Form.Label>
      <InputGroup>
        <Form.Control
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
        />
        <Button
          type="button"
          variant="outline-primary"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? <Spinner animation="border" size="sm" /> : 'Upload'}
        </Button>
      </InputGroup>
      <Form.Control
        ref={inputRef}
        type="file"
        accept="image/*,.ico"
        className="d-none"
        onChange={(e) => uploadFile(e.target.files?.[0])}
      />
      {value ? (
        <div className="mt-2 rounded-3 border bg-white p-2 text-center">
          <img
            src={value}
            alt={`${label} preview`}
            style={{ maxHeight: 42, maxWidth: '100%', objectFit: 'contain' }}
          />
        </div>
      ) : null}
    </Form.Group>
  );
}
